/**
 * Meniul vitrinei alese.
 *
 * Ce se arata aici despre vanzator e o PREVIZUALIZARE, marcata ca atare
 * (ADR-003). Alegerea se face la checkout, cu cosul final. Pagina asta NU
 * accepta vanzatorul in numele clientului.
 *
 * Trei decizii de flux care nu sunt cosmetice:
 *
 *  1. **Adresa nu e un zid.** Meniul se rasfoieste si se pune in cos fara
 *     adresa. Produsele intra ca linii NEVERIFICATE si se reverifica contra
 *     meniului adresei imediat ce ea apare — la orice moment, din orice pagina.
 *     Varianta anterioara deschidea formularul de adresa la primul „+", adica
 *     intrerupea clientul exact in momentul in care se hotarase.
 *  2. **Confirmarea adaugarii se vede acolo unde s-a apasat.** O notificare jos,
 *     nu o banda in capul paginii: pe 477 de produse, banda de sus nu se vedea de
 *     la mijlocul listei, iar clientul apasa din nou.
 *  3. **Meniul mare are doua niveluri.** Cand categoriile sunt multe si au
 *     parinti diferiti (BAR / BUCATARIE), parintii devin un comutator, iar bara
 *     de categorii arata doar copiii parintelui ales. 43 de chipuri intr-o bara
 *     nu sunt o navigare, sunt o lista de derulat.
 *
 * Meniul e construit pentru sute de produse: sectiunile primesc
 * `content-visibility` (`.da-defer`), pozele sunt lenese in afara primului
 * ecran, iar filtrarea la scris se face pe un index normalizat.
 */

import { useCallback, useDeferredValue, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

import { formatRon, type MenuDto, type OfferDto, type StorefrontSummaryDto } from "@deliveryapp/shared";

import { fetchStorefront } from "../api/client";
import { useAddressSheet } from "../components/AddressSheetProvider";
import { formatAddressShort } from "../components/AddressForm";
import { useBootstrap } from "../components/BootstrapProvider";
import { useCart } from "../components/CartProvider";
import { CategoryRail, useActiveSection, type CategoryRailItem } from "../components/CategoryRail";
import { IconArrowLeft, IconBike, IconClock, IconClose, IconInfo, IconPin, IconSearch, IconWarning } from "../components/icons";
import { ResponsiveImage } from "../components/Media";
import { Modal } from "../components/Modal";
import { OfferCard, type OfferAddPayload } from "../components/OfferCard";
import { favoritesStorageKey, readFavorites, writeFavorites } from "../components/favorites";
import { addressKeyOf, refreshedAddPayload } from "../components/offer-refresh";
import {
  availableOfferFilters,
  offerMatchesFilter,
  offerPromotion,
  type OfferFilter,
} from "../components/offer-merchandising";
import { recommendedOffers } from "../components/recommendations";
import { SellerIdentityCard } from "../components/SellerIdentityCard";
import { OfferGridSkeleton, VendorHeaderSkeleton } from "../components/Skeleton";
import { EmptyState, ErrorState } from "../components/StateScreen";
import { useToast } from "../components/ToastProvider";
import { Button, Chip, IconButton } from "../components/ui";
import { useAddressMenu, useCartRevalidation } from "../components/useAddressMenu";
import { useLoadable } from "../components/useLoadable";
import { menusVisibleAtAddress } from "../components/vendor-address-menus";

export { refreshedAddPayload } from "../components/offer-refresh";

/** Text pregatit pentru cautare: fara diacritice, litere mici. */
function normalize(value: string): string {
  return value
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase();
}

/** „BUCATARIE" -> „Bucatarie", „gin & drive" -> „Gin & drive". */
function titleCase(value: string): string {
  const lower = value.toLocaleLowerCase("ro-RO").trim();
  return lower.length === 0 ? lower : lower.charAt(0).toLocaleUpperCase("ro-RO") + lower.slice(1);
}

interface MenuSection {
  /** `id` in DOM: tinta saltului din bara si a urmaririi derularii. */
  id: string;
  label: string;
  /** Primul segment al caii de categorie („BAR", „BUCATARIE"), pentru nivelul 1. */
  group: string;
  offers: OfferDto[];
}

/**
 * Un `id` valid si STABIL pentru o sectiune. Se compune din meniu + categorie,
 * niciodata din pozitia in lista: pozitia se schimba la fiecare litera scrisa in
 * cautare, iar `id`-ul e si cheia React a sectiunii.
 */
function sectionId(menuId: string, category: string): string {
  const slug = normalize(category).replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
  return `cat-${normalize(menuId).replace(/[^a-z0-9]+/g, "-")}-${slug || "meniu"}`;
}

function buildSections(
  menus: MenuDto[],
  search: string,
  activeFilter: OfferFilter,
  favorites: ReadonlySet<string>,
): MenuSection[] {
  const needle = normalize(search.trim());
  const sections: MenuSection[] = [];
  const used = new Set<string>();
  const labels = new Set<string>();
  const multiple = menus.length > 1;

  for (const menu of menus) {
    const groups = new Map<string, OfferDto[]>();
    for (const offer of [...menu.offers].sort((a, b) => a.sortOrder - b.sortOrder)) {
      if (!offerMatchesFilter(offer, activeFilter, favorites)) continue;
      if (needle.length > 0) {
        const optionNames = offer.optionGroups.flatMap((group) => group.options.map((option) => option.name));
        const haystack = normalize(
          `${offer.name} ${offer.description ?? ""} ${offer.categoryPath} ${offer.dietTags.join(" ")} ${offer.allergens.join(" ")} ${optionNames.join(" ")}`,
        );
        if (!haystack.includes(needle)) continue;
      }
      const key = offer.categoryPath.length > 0 ? offer.categoryPath : "Meniu";
      const bucket = groups.get(key);
      if (bucket === undefined) groups.set(key, [offer]);
      else bucket.push(offer);
    }
    for (const [category, offers] of groups) {
      let id = sectionId(menu.menuId, category);
      if (used.has(id)) {
        let suffix = 2;
        while (used.has(`${id}-${suffix}`)) suffix += 1;
        id = `${id}-${suffix}`;
      }
      used.add(id);
      const parts = category.split("/").filter((part) => part.length > 0);
      const leaf = parts[parts.length - 1] ?? category;
      // Doua ramuri cu aceeasi frunza („Vinuri/Rosu", „Cocktailuri/Rosu"): se
      // adauga parintele, doar atunci.
      const label = labels.has(leaf) ? parts.slice(-2).join(" · ") || leaf : leaf;
      labels.add(leaf);
      sections.push({
        id,
        label: multiple ? `${menu.name} · ${titleCase(label)}` : titleCase(label),
        group: parts.length > 1 ? (parts[0] ?? "") : "",
        offers,
      });
    }
  }
  return sections;
}

/** Nivelul 1 al navigarii: parintii categoriilor, cand merita afisati. */
function topLevelGroups(sections: MenuSection[], mode: string | undefined): string[] {
  if (mode === "flat") return [];
  const groups = [...new Set(sections.map((section) => section.group).filter((group) => group.length > 0))];
  // Doua sau mai multe ramuri si destule frunze incat bara sa fie greu de
  // derulat: sub pragul asta, o singura bara e mai simpla decat doua.
  if (groups.length < 2 || sections.length < 8) return [];
  return groups;
}

export function Vendor() {
  const { slug = "" } = useParams<{ slug: string }>();
  const { marketKey, config, bootstrap } = useBootstrap();
  const { session, selectStorefront, addLine, setQty } = useCart();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const [activeFilter, setActiveFilter] = useState<OfferFilter>("all");
  const [activeGroup, setActiveGroup] = useState<string | null>(null);
  const addressSheet = useAddressSheet();
  const [infoOpen, setInfoOpen] = useState(false);
  const favoriteKey = favoritesStorageKey(marketKey, slug);
  const [favoriteOfferIds, setFavoriteOfferIds] = useState<Set<string>>(() => readFavorites(favoriteKey));

  const summary: StorefrontSummaryDto | null = bootstrap.storefronts.find((item) => item.slug === slug) ?? null;
  const single = bootstrap.storefronts.length === 1;

  useEffect(() => {
    setFavoriteOfferIds(readFavorites(favoriteKey));
    setActiveFilter("all");
    setActiveGroup(null);
    setSearch("");
  }, [favoriteKey]);

  const storefront = useLoadable((signal) => fetchStorefront(marketKey, slug, signal), [marketKey, slug]);
  const storefrontName = storefront.data?.name ?? summary?.name ?? null;
  useEffect(() => {
    if (slug.length > 0) selectStorefront(slug, storefrontName);
  }, [slug, storefrontName, selectStorefront]);

  const address = session.address;
  const addressKey = addressKeyOf(address);
  const addressMenu = useAddressMenu(slug, storefront.data !== null);

  // Reverificarea cosului contra meniului adresei, cu anunt o singura data.
  useCartRevalidation(addressMenu, (result) => {
    if (result.unavailable.length > 0) {
      toast({
        key: "revalidare",
        tone: "warning",
        title:
          result.unavailable.length === 1
            ? `„${result.unavailable[0]}" nu e disponibil la adresa ta`
            : `${result.unavailable.length} produse nu sunt disponibile la adresa ta`,
        description: "Le vezi marcate în coș. Restul comenzii rămâne.",
        action: { label: "Vezi coșul", onClick: () => navigate("/cos") },
        durationMs: 6000,
      });
    } else if (result.changed > 0) {
      toast({ key: "revalidare", tone: "info", title: "Coșul a fost actualizat pentru adresa ta" });
    }
  });

  const knownSeller = storefront.data?.seller ?? null;
  const seller = knownSeller ?? addressMenu.preview?.seller ?? null;
  const activeZone = addressMenu.preview?.zone ?? null;
  const etaMinutes = addressMenu.preview?.eta.totalMinutes ?? null;

  const menus = useMemo(() => {
    const source = addressMenu.menu?.menus ?? storefront.data?.menus ?? [];
    return menusVisibleAtAddress(source, addressMenu.menu !== null);
  }, [addressMenu.menu, storefront.data]);
  const allOffers = useMemo(() => menus.flatMap((menu) => menu.offers), [menus]);

  const deferredSearch = useDeferredValue(search);
  const allSections = useMemo(
    () => buildSections(menus, deferredSearch, activeFilter, favoriteOfferIds),
    [menus, deferredSearch, activeFilter, favoriteOfferIds],
  );
  const groups = useMemo(() => topLevelGroups(allSections, config.layout.menuNavigationMode), [allSections, config.layout.menuNavigationMode]);
  const searching = deferredSearch.trim().length > 0;
  const filtering = activeFilter !== "all";
  // Nivelul 1 se aplica doar la rasfoire: cautarea si filtrele trec peste ramuri.
  const sections = useMemo(
    () => (activeGroup === null || searching || filtering ? allSections : allSections.filter((section) => section.group === activeGroup)),
    [allSections, activeGroup, searching, filtering],
  );
  const totalOffers = allOffers.length;
  const merchandisingFilters = useMemo(() => availableOfferFilters(allOffers), [allOffers]);
  const promotedOffers = useMemo(
    () => allOffers.filter((offer) => offer.available && offerPromotion(offer) !== null).slice(0, 8),
    [allOffers],
  );

  const railItems = useMemo<CategoryRailItem[]>(
    () => sections.map((section) => ({ id: section.id, label: section.label, count: section.offers.length })),
    [sections],
  );
  const sectionIds = useMemo(() => sections.map((section) => section.id), [sections]);
  const activeSection = useActiveSection(sectionIds);

  const qtyByOffer = useMemo(() => {
    const map = new Map<string, number>();
    for (const line of session.lines) map.set(line.offerId, (map.get(line.offerId) ?? 0) + line.qty);
    return map;
  }, [session.lines]);

  const cartOfferIds = useMemo(() => new Set(session.lines.map((line) => line.offerId)), [session.lines]);
  const suggestions = useMemo(() => recommendedOffers(menus, cartOfferIds, 4), [menus, cartOfferIds]);

  const toggleFavorite = (offerId: string): void => {
    setFavoriteOfferIds((current) => {
      const next = new Set(current);
      if (next.has(offerId)) next.delete(offerId);
      else next.add(offerId);
      writeFavorites(favoriteKey, next);
      return next;
    });
  };

  const commitOffer = useCallback(
    (offer: OfferDto, payload: OfferAddPayload, verifiedForAddressKey: string | null): void => {
      addLine(
        {
          lineId: payload.lineId,
          offerId: offer.offerId,
          skuId: offer.skuId,
          name: offer.name,
          unitPriceGross: payload.unitPriceGross,
          compareAtUnitPrice: payload.compareAtUnitPrice,
          notes: payload.notes ?? null,
          imageUrl: payload.imageUrl,
          selectedOptions: payload.options.map((option) => ({
            groupId: option.groupId,
            optionId: option.optionId,
            priceDelta: option.priceDelta,
          })),
          optionLabels: payload.options.map((option) => `${option.groupName}: ${option.optionName}`),
          maxQty: offer.maxQty,
          minQty: offer.minQty,
          verifiedForAddressKey,
        },
        payload.qty,
        offer.maxQty,
      );
    },
    [addLine],
  );

  const announceAdded = (offer: OfferDto, qty: number, verified: boolean): void => {
    const inCart = (qtyByOffer.get(offer.offerId) ?? 0) + qty;
    toast({
      key: `adaugat-${offer.offerId}`,
      tone: "success",
      title: inCart > 1 ? `${offer.name} · ${inCart} în coș` : `${offer.name} adăugat în coș`,
      description: verified
        ? undefined
        : address === null
          ? "Adaugă adresa ca să confirmăm disponibilitatea și livrarea."
          : "Îl verificăm la adresa ta.",
      action:
        address === null
          ? { label: "Adaugă adresa", onClick: () => addressSheet.open() }
          : { label: "Vezi coșul", onClick: () => navigate("/cos") },
    });
  };

  const addOffer = (offer: OfferDto, payload: OfferAddPayload): void => {
    if (address === null) {
      // Fara adresa: intra neverificat, se reverifica la prima adresa.
      commitOffer(offer, payload, null);
      announceAdded(offer, payload.qty, false);
      return;
    }
    if (addressMenu.outOfCoverage) {
      toast({
        key: "acoperire",
        tone: "warning",
        title: "Adresa ta e în afara zonei de livrare",
        description: "Schimbă adresa ca să poți comanda de aici.",
        action: { label: "Schimbă adresa", onClick: () => addressSheet.open() },
      });
      return;
    }
    if (addressMenu.menu === null) {
      // Meniul adresei inca se incarca: intra neverificat, revalidarea il prinde.
      commitOffer(offer, payload, null);
      announceAdded(offer, payload.qty, false);
      return;
    }
    const refreshedOffer = addressMenu.menu.menus.flatMap((menu) => menu.offers).find((candidate) => candidate.offerId === offer.offerId);
    const refreshed = refreshedOffer === undefined ? null : refreshedAddPayload(refreshedOffer, payload);
    if (refreshedOffer === undefined || refreshed === null) {
      toast({
        key: `adaugat-${offer.offerId}`,
        tone: "warning",
        title: "Produsul nu e disponibil la adresa ta",
        description: "Combinația aleasă nu poate fi pregătită de restaurantele care livrează aici.",
      });
      return;
    }
    commitOffer(refreshedOffer, refreshed, addressKey);
    announceAdded(refreshedOffer, refreshed.qty, true);
  };

  const decreaseOffer = (offer: OfferDto): void => {
    const next = (qtyByOffer.get(offer.offerId) ?? 0) - 1;
    setQty(offer.offerId, next < offer.minQty ? 0 : next, offer.maxQty);
  };

  if (storefront.error !== null) {
    return <ErrorState title="Meniul nu a putut fi încărcat" error={storefront.error} onRetry={storefront.reload} />;
  }

  if (storefront.data === null) {
    return (
      <div className="flex flex-col gap-6" aria-busy="true">
        <span className="sr-only" role="status">
          Se încarcă meniul
        </span>
        <VendorHeaderSkeleton />
        <OfferGridSkeleton />
      </div>
    );
  }

  const hero = storefront.data.heroImageUrl;
  const hasHero = typeof hero === "string" && hero.length > 0;
  const listing = summary?.listing ?? null;
  const visibleOfferCount = sections.reduce((total, section) => total + section.offers.length, 0);
  const deliveryLoading = address !== null && !addressMenu.outOfCoverage && (addressMenu.previewState === "loading" || addressMenu.menuState === "loading");
  const addressPending = address === null || addressMenu.menu === null;

  const offerCardFor = (offer: OfferDto, extra: { priority?: boolean } = {}) => (
    <OfferCard
      key={offer.offerId}
      offer={offer}
      qtyInCart={qtyByOffer.get(offer.offerId) ?? 0}
      favorite={favoriteOfferIds.has(offer.offerId)}
      onToggleFavorite={() => toggleFavorite(offer.offerId)}
      priority={extra.priority === true}
      addressPending={addressPending}
      onAdd={(payload) => addOffer(offer, payload)}
      onDecrease={() => decreaseOffer(offer)}
    />
  );

  return (
    <div className="flex flex-col gap-4">
      {/* Antetul vitrinei: poza de coperta (cand exista) cu numele peste ea, sau
          un rand compact; sub el, datele de livrare intr-o singura banda. */}
      <header className="flex flex-col gap-3">
        <div className="flex items-center justify-between gap-2">
          <Link to="/" className="inline-flex items-center gap-1 text-sm font-semibold text-ink-muted hover:text-ink">
            <IconArrowLeft size={18} /> {single ? "Acasă" : "Restaurante"}
          </Link>
          <IconButton label="Informații despre restaurant" variant="ghost" size={40} onClick={() => setInfoOpen(true)}>
            <IconInfo size={22} />
          </IconButton>
        </div>

        {hasHero ? (
          <div className="relative -mx-4 overflow-hidden sm:mx-0 sm:rounded-card">
            <ResponsiveImage src={hero} alt="" ratio="16/7" sizes="(min-width: 72rem) 72rem, 100vw" priority rounded={false} />
            <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent p-4 pt-14 sm:p-6">
              <h1 className="da-hero-text text-2xl font-extrabold text-white sm:text-4xl">{storefront.data.name}</h1>
              {listing === null || listing.tags.length === 0 ? null : (
                <p className="da-hero-text mt-1 text-sm font-medium text-white/90">{listing.tags.join(" · ")}</p>
              )}
            </div>
          </div>
        ) : (
          <div>
            <h1 className="text-2xl font-extrabold text-ink sm:text-3xl">{storefront.data.name}</h1>
            <p className="text-sm text-ink-muted">
              {listing !== null && listing.tags.length > 0 ? listing.tags.join(" · ") : `${totalOffers} produse · comandă online cu livrare`}
            </p>
          </div>
        )}

        {/* Banda de livrare: o singura linie cu ce conteaza, apasabila. */}
        {address === null ? (
          <button
            type="button"
            onClick={() => addressSheet.open()}
            className="da-card da-card-link da-tap flex w-full items-center gap-3 p-3 text-left"
          >
            <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-brand-soft text-brand-on-surface">
              <IconPin size={20} />
            </span>
            <span className="min-w-0 flex-1">
              <span className="block text-sm font-bold text-ink">Adaugă adresa ca să vezi timpul și taxa de livrare</span>
              <span className="block text-xs text-ink-muted">
                {listing !== null && listing.etaMinutesFrom !== null && listing.etaMinutesTo !== null
                  ? `Orientativ ${listing.etaMinutesFrom}–${listing.etaMinutesTo} min${listing.deliveryFeeFrom !== null ? ` · livrare de la ${formatRon(listing.deliveryFeeFrom)}` : ""}`
                  : "Meniul poate fi răsfoit și pus în coș și înainte."}
              </span>
            </span>
            <span className="shrink-0 rounded-control bg-brand px-3 py-2 text-sm font-bold text-brand-contrast">Adaugă</span>
          </button>
        ) : addressMenu.outOfCoverage ? (
          <div className="flex flex-wrap items-center gap-3 rounded-card border border-warning bg-surface p-3">
            <IconWarning size={22} className="shrink-0 text-warning" />
            <div className="min-w-0 flex-1">
              <p className="text-sm font-bold text-ink">
                {addressMenu.previewError?.code === "NO_VENDOR_AVAILABLE" ? "Momentan nu putem livra la adresa ta" : "Adresa ta este în afara zonei de livrare"}
              </p>
              <p className="text-xs text-ink-muted">{`${formatAddressShort(address)}, ${address.city}`}</p>
            </div>
            <Button size="sm" variant="secondary" onClick={() => addressSheet.open()}>
              Schimbă adresa
            </Button>
          </div>
        ) : addressMenu.previewState === "error" && addressMenu.previewError !== null ? (
          <div className="flex flex-wrap items-center gap-3 rounded-card border border-danger bg-surface p-3">
            <IconWarning size={22} className="shrink-0 text-danger" />
            <p className="min-w-0 flex-1 text-sm text-ink">Nu am putut verifica livrarea la adresa ta. {addressMenu.previewError.message}</p>
            {addressMenu.previewError.retryable ? (
              <Button size="sm" variant="secondary" onClick={addressMenu.reload}>
                Încearcă din nou
              </Button>
            ) : null}
          </div>
        ) : (
          <button
            type="button"
            onClick={() => addressSheet.open()}
            className="da-card da-card-link da-tap flex w-full items-center gap-3 p-3 text-left"
            aria-label="Schimbă adresa de livrare"
          >
            <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-brand-soft text-brand-on-surface">
              <IconPin size={20} />
            </span>
            <span className="min-w-0 flex-1">
              <span className="da-clamp-1 block text-sm font-bold text-ink">{`${formatAddressShort(address)}, ${address.city}`}</span>
              {deliveryLoading || activeZone === null ? (
                <span className="da-shimmer mt-1 block h-3 w-1/2 rounded" aria-hidden="true" />
              ) : (
                <span className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-ink-muted">
                  {etaMinutes === null ? null : (
                    <span className="inline-flex items-center gap-1 font-semibold text-ink">
                      <IconClock size={14} /> ~{etaMinutes} min
                    </span>
                  )}
                  <span className="inline-flex items-center gap-1">
                    <IconBike size={14} /> {activeZone.deliveryFee === 0 ? "gratuit" : formatRon(activeZone.deliveryFee)}
                  </span>
                  {activeZone.minOrderValue > 0 ? <span>{`min. ${formatRon(activeZone.minOrderValue)}`}</span> : null}
                  {activeZone.freeDeliveryThreshold === null || activeZone.freeDeliveryThreshold === undefined ? null : (
                    <span className="text-success">{`gratuit peste ${formatRon(activeZone.freeDeliveryThreshold)}`}</span>
                  )}
                </span>
              )}
            </span>
            <span className="shrink-0 text-sm font-bold text-brand-on-surface">Schimbă</span>
          </button>
        )}
      </header>

      {totalOffers === 0 ? (
        <EmptyState title="Meniul este gol" description="Vitrina nu are niciun produs publicat în acest moment." />
      ) : (
        <>
          {/* Cautare + filtre + (nivelul 1) + bara de categorii. Bara ramane
              lipita sub antet; cautarea se deschide dintr-un buton ca sa nu
              consume un rand pe telefon pana nu e nevoie de ea. */}
          <div className="flex flex-col gap-2">
            {searchOpen || search.length > 0 ? (
              <div className="relative">
                <label htmlFor="cauta-in-meniu" className="sr-only">
                  Caută în meniu
                </label>
                <span className="pointer-events-none absolute inset-y-0 left-3.5 flex items-center text-ink-muted">
                  <IconSearch size={20} />
                </span>
                <input
                  id="cauta-in-meniu"
                  type="search"
                  autoFocus
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder={`Caută în meniu (${totalOffers} produse)`}
                  className="w-full rounded-full border border-line bg-canvas py-3 pl-11 pr-11 text-base text-ink placeholder:text-ink-muted focus:border-brand"
                />
                <button
                  type="button"
                  onClick={() => {
                    setSearch("");
                    setSearchOpen(false);
                  }}
                  aria-label="Închide căutarea"
                  className="absolute inset-y-0 right-2 grid w-9 place-items-center text-ink-muted hover:text-ink"
                >
                  <IconClose size={18} />
                </button>
              </div>
            ) : null}

            <div className="da-rail da-rail-bleed items-center py-0.5" role="group" aria-label="Filtre pentru meniu">
              {searchOpen || search.length > 0 ? null : (
                <Chip onClick={() => setSearchOpen(true)} aria-label="Caută în meniu">
                  <IconSearch size={16} /> Caută
                </Chip>
              )}
              {merchandisingFilters
                .filter((filter) => filter.id !== "all")
                .map((filter) => (
                  <Chip key={filter.id} selected={filter.id === activeFilter} onClick={() => setActiveFilter(filter.id === activeFilter ? "all" : filter.id)}>
                    {filter.icon.length === 0 ? null : <span aria-hidden="true">{filter.icon}</span>}
                    {filter.label}
                  </Chip>
                ))}
            </div>

            {groups.length === 0 || searching || filtering ? null : (
              <div className="flex gap-1 rounded-full bg-surface p-1" role="tablist" aria-label="Secțiuni ale meniului">
                {[null, ...groups].map((group) => {
                  const selected = activeGroup === group;
                  return (
                    <button
                      key={group ?? "toate"}
                      type="button"
                      role="tab"
                      aria-selected={selected}
                      onClick={() => setActiveGroup(group)}
                      className={[
                        "da-press flex-1 rounded-full px-3 py-2 text-sm font-bold transition-colors",
                        selected ? "bg-canvas text-ink shadow-sm" : "text-ink-muted hover:text-ink",
                      ].join(" ")}
                    >
                      {group === null ? "Toate" : titleCase(group)}
                    </button>
                  );
                })}
              </div>
            )}

            <p aria-live="polite" className="min-h-4 text-xs text-ink-muted">
              {searching || filtering ? `${visibleOfferCount} produse găsite` : ""}
            </p>
          </div>

          <CategoryRail items={railItems} active={activeSection} />

          {!searching && !filtering && promotedOffers.length > 0 ? (
            <section className="overflow-hidden rounded-card bg-brand-soft p-3 sm:p-4" aria-labelledby="oferte-titlu">
              <div className="mb-3 flex items-end justify-between gap-3">
                <div>
                  <p className="text-xs font-extrabold uppercase tracking-wider text-brand-on-surface">Economii reale</p>
                  <h2 id="oferte-titlu" className="text-lg font-extrabold text-ink">Oferte pentru tine</h2>
                </div>
                <button type="button" onClick={() => setActiveFilter("promotions")} className="text-sm font-bold text-brand-on-surface hover:underline">
                  Vezi toate
                </button>
              </div>
              <ul className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-4">
                {promotedOffers.slice(0, 4).map((offer) => offerCardFor(offer))}
              </ul>
            </section>
          ) : null}

          {!searching && !filtering && suggestions.length > 0 ? (
            <section className="flex flex-col gap-3" aria-labelledby="recomandari-titlu">
              <div>
                <h2 id="recomandari-titlu" className="text-lg font-bold text-ink">
                  {cartOfferIds.size > 0 ? "Merge bine cu ce ai în coș" : "Recomandate"}
                </h2>
              </div>
              <ul className="grid grid-cols-1 gap-2 sm:grid-cols-2 xl:grid-cols-4">
                {suggestions.map((offer, index) => offerCardFor(offer, { priority: !hasHero && index < 2 }))}
              </ul>
            </section>
          ) : null}

          {sections.length === 0 ? (
            <EmptyState
              title={activeFilter === "favorites" ? "Nu ai produse favorite încă" : "Niciun produs pentru selecția ta"}
              description={
                activeFilter === "favorites"
                  ? "Apasă inima de pe orice produs și îl vei găsi rapid aici, pe acest dispozitiv."
                  : "Încearcă alt cuvânt sau elimină filtrul ca să vezi meniul întreg."
              }
              action={
                <Button
                  onClick={() => {
                    setSearch("");
                    setActiveFilter("all");
                    setActiveGroup(null);
                  }}
                >
                  Vezi meniul întreg
                </Button>
              }
            />
          ) : (
            sections.map((section, sectionIndex) => (
              <section key={section.id} id={section.id} aria-labelledby={`${section.id}-titlu`} className="da-anchor flex flex-col gap-3">
                <h2 id={`${section.id}-titlu`} className="text-lg font-bold text-ink">
                  {section.label}
                  <span className="ml-2 text-sm font-normal text-ink-muted">{section.offers.length}</span>
                </h2>
                <ul className="grid grid-cols-1 gap-2 sm:grid-cols-2 sm:gap-3 lg:grid-cols-3 xl:grid-cols-4">
                  {section.offers.map((offer, offerIndex) => offerCardFor(offer, { priority: !hasHero && sectionIndex === 0 && offerIndex < 2 }))}
                </ul>
              </section>
            ))
          )}
        </>
      )}

      {/* Foaia de informatii: cine vinde (ADR-003, previzualizare), zonele si
          conditiile de livrare, alergeni, contact. */}
      <Modal open={infoOpen} onClose={() => setInfoOpen(false)} title={storefront.data.name}>
        <div className="flex flex-col gap-4">
          {seller === null ? (
            <p className="text-sm text-ink-muted">
              {storefront.data.catalogMode === "shared"
                ? "Restaurantul care îți pregătește comanda se alege în funcție de adresă și de încărcarea bucătăriilor. Îl vezi, cu datele complete, la finalizare."
                : "Restaurantul care îți vinde se afișează, cu datele complete, la finalizare."}
            </p>
          ) : (
            <SellerIdentityCard
              sellers={[seller]}
              heading={storefront.data.catalogMode === "shared" ? "Cine pregătește comanda (estimare)" : "Vânzător"}
              supportPhone={config.identity.supportPhone}
            />
          )}
          {storefront.data.zones.length === 0 ? null : (
            <section aria-labelledby="zone-titlu">
              <h3 id="zone-titlu" className="text-sm font-bold text-ink">Unde livrează</h3>
              <ul className="mt-2 divide-y divide-line rounded-card border border-line bg-surface">
                {storefront.data.zones.map((zone) => (
                  <li key={zone.zoneId} className="flex items-center justify-between gap-3 px-3 py-2 text-sm">
                    <span className="text-ink">{zone.name}{zone.city.length > 0 && zone.city !== zone.name ? `, ${zone.city}` : ""}</span>
                    <span className="text-ink-muted">
                      {zone.deliveryFee === 0 ? "livrare gratuită" : `livrare ${formatRon(zone.deliveryFee)}`}
                      {zone.minOrderValue > 0 ? ` · min. ${formatRon(zone.minOrderValue)}` : ""}
                    </span>
                  </li>
                ))}
              </ul>
            </section>
          )}
          <p className="text-xs leading-relaxed text-ink-muted">
            Alergenii sunt declarați pe fiecare produs. Dacă ai o alergie severă, sună restaurantul înainte de comandă: în bucătărie poate exista contaminare încrucișată.
          </p>
          {config.identity.supportPhone === undefined ? null : (
            <a className="text-sm font-semibold text-brand-on-surface underline" href={`tel:${config.identity.supportPhone.replace(/\s+/g, "")}`}>
              {`Asistență: ${config.identity.supportPhone}`}
            </a>
          )}
        </div>
      </Modal>

    </div>
  );
}
